import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { MessageSquare, MoreHorizontal, MoreVertical, Plus, Target } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import type { KrCheckinRead } from "@chai/shared";

import { Skeleton } from "@/components/ui/skeleton";

import {
  createCheckin,
  deleteKeyResult,
  getKeyResult,
  getKeyResultActivity,
  getObjectiveBySlug,
  patchInitiative,
  patchKeyResult,
  promoteInitiative,
  reweightInitiativeLink,
  unlinkInitiative,
} from "@/api/plan";
import { canEdit } from "@/components/chat/railMode";
import { Comments } from "@/components/plan/Comments";
import { DeskScreen } from "@/components/plan/DeskScreen";
import { StatusPill } from "@/components/plan/StatusPill";
import { barFill, confidenceLabel, humanizeStatus, pillClass } from "@/components/plan/plan-status";
import { useDocumentTitle } from "@/lib/document-title";

export const Route = createFileRoute("/_app/plan/o/$objectiveSlug/kr/$krId")({
  component: KeyResultDetail,
});

const CONFIDENCE_OPTIONS = ["on_track", "at_risk", "off_track"] as const;

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : Number(n).toFixed(1));
const withUnit = (n: number, unit: string | null | undefined) =>
  unit === "%" ? `${fmt(n)}%` : unit ? `${fmt(n)} ${unit}` : fmt(n);
const fmtDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};
// "3 days ago" relative timestamp for the activity feed.
const relAt = (iso: string) => {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
};
const initials = (n: string | null | undefined) =>
  (n || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
// InitiativeStatus enum (proposed | reviewing | promoted) → mockup pill modifier.
const initStatusCls = (s: string | null | undefined) => (s ? s.toLowerCase() : "proposed");

// ── Weighted execution-support roll-up (mirrors the mockup's okrWeight.rollup) ──
// Progress is DERIVED, never entered. Each contributor may carry a weight; if none
// do, contributors are weighted equally. Mixed: unset contributors split the
// remainder up to 100; shares are normalized so they always sum to ~100%.
interface Contributor {
  id: string;
  name: string;
  progress: number;
  weight: number;
  kind: "project" | "initiative";
}
interface RollupItem extends Contributor {
  weightPct: number;
  contribution: number;
}
function rollup(contributors: Contributor[]): {
  progress: number;
  weighted: boolean;
  items: RollupItem[];
} {
  if (!contributors.length) return { progress: 0, weighted: false, items: [] };

  const raw = contributors.map((c) => (typeof c.weight === "number" && c.weight > 0 ? c.weight : null));
  const weighted = raw.some((w) => w != null);

  let eff: number[];
  if (!weighted) {
    eff = contributors.map(() => 1);
  } else {
    const setSum = raw.reduce<number>((a, w) => a + (w ?? 0), 0);
    const nNull = raw.filter((w) => w == null).length;
    const fill = nNull > 0 ? Math.max(0, 100 - setSum) / nNull : 0;
    eff = raw.map((w) => (w != null ? w : fill));
  }

  const sum = eff.reduce((a, b) => a + b, 0) || 1;
  const items: RollupItem[] = contributors.map((c, i) => {
    const share = (eff[i] ?? 0) / sum;
    const progress = c.progress || 0;
    return { ...c, progress, weightPct: Math.round(share * 100), contribution: progress * share };
  });
  const progress = Math.round(items.reduce((a, it) => a + it.contribution, 0));
  return { progress, weighted, items };
}

// ── Lightweight overflow menu (mockup .pc-overflow + .card-menu) ────────────────
interface MenuItem {
  label?: string;
  sep?: boolean;
  danger?: boolean;
  onClick?: () => void;
}
function MoreMenu({ items, title = "Actions" }: { items: MenuItem[]; title?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <span className="kr-more-wrap" ref={ref}>
      <button
        type="button"
        className="pc-overflow"
        title={title}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        <MoreHorizontal size={15} />
      </button>
      {open && (
        <div className="card-menu" style={{ top: "calc(100% + 6px)", bottom: "auto" }}>
          {items.map((it, i) =>
            it.sep ? (
              <div key={i} className="cm-sep" />
            ) : (
              <button
                key={i}
                type="button"
                className={`cm-item ${it.danger ? "danger" : ""}`}
                onClick={() => {
                  setOpen(false);
                  it.onClick?.();
                }}
              >
                {it.label}
              </button>
            ),
          )}
        </div>
      )}
    </span>
  );
}

// A single check-in history item with an inline, expandable comment thread anchored
// to the check-in (real backing via Comments targetKind="checkin"). The check-in note
// is the owner's update; comments are discussion. Mirrors the mockup CheckinItem.
function CheckinItem({ c, unit }: { c: KrCheckinRead; unit: string | null }) {
  const [open, setOpen] = useState(false);
  const authorName = c.author_name ?? "Unknown";
  return (
    <div className="checkin-item">
      <span className="mini-avatar sm">{initials(authorName)}</span>
      <div className="checkin-body">
        <div className="checkin-top">
          <b>{authorName}</b>
          <span className="checkin-val">{withUnit(c.value, unit)}</span>
          {c.confidence && (
            <StatusPill
              status={c.confidence}
              label={confidenceLabel(c.confidence)}
              className="checkin-conf"
            />
          )}
          {c.created_at && <span className="checkin-at">{fmtDate(c.created_at)}</span>}
        </div>
        {c.note && <div className="checkin-note">{c.note}</div>}
        {c.blocker && <div className="checkin-blocker">⚠ {c.blocker}</div>}
        <button type="button" className="checkin-cmt-toggle" onClick={() => setOpen((o) => !o)}>
          <MessageSquare size={12} />{" "}
          {c.comment_count > 0
            ? `${c.comment_count} comment${c.comment_count === 1 ? "" : "s"}`
            : "Comment"}
        </button>
        {open && (
          <div className="checkin-cmts">
            <Comments
              targetKind="checkin"
              targetId={c.id}
              placeholder="Comment on this check-in…"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function KeyResultDetail() {
  const { objectiveSlug, krId } = useParams({
    from: "/_app/plan/o/$objectiveSlug/kr/$krId",
  });
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const key = ["plan", "kr", krId];

  const detail = useQuery({ queryKey: key, queryFn: () => getKeyResult(krId) });
  // Parent objective — name for the back link + the KR order (KR-N refs). Reuses
  // the warm objective-by-slug cache the objective page already populated.
  const objQuery = useQuery({
    queryKey: ["plan", "objective-by-slug", objectiveSlug],
    queryFn: () => getObjectiveBySlug(objectiveSlug),
  });
  const objName = objQuery.data?.objective.name ?? "Objective";
  const objOwner = objQuery.data?.objective.owner_name ?? null;
  const objPeriod = objQuery.data?.objective.period ?? null;
  const krList = objQuery.data?.key_results ?? [];

  // Real KR activity feed (progress raised, projects linked, check-ins recorded).
  const activityQuery = useQuery({
    queryKey: ["plan", "kr-activity", krId],
    queryFn: () => getKeyResultActivity(krId),
  });
  const activity = activityQuery.data ?? [];
  const refOf = (id: string) => {
    const i = krList.findIndex((k) => k.id === id);
    return i >= 0 ? `KR-${i + 1}` : null;
  };

  const [value, setValue] = useState("");
  const [confidence, setConfidence] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [blocker, setBlocker] = useState("");
  const [execOpen, setExecOpen] = useState(false);
  // Check-in composer is hidden until "+ Check in" is clicked (mockup).
  const [checkingIn, setCheckingIn] = useState(false);
  const [krMenuOpen, setKrMenuOpen] = useState(false);
  const krMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (krMenuRef.current && !krMenuRef.current.contains(e.target as Node)) setKrMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  // Activity section collapse — supporting context, kept below the fold.
  const [actOpen, setActOpen] = useState(false);

  // Seed the composer's value with the KR's current value once loaded.
  useEffect(() => {
    if (detail.data && value === "") {
      setValue(String(detail.data.key_result.current_value));
      setConfidence(detail.data.key_result.confidence ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.data?.key_result.id]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: ["plan", "kr-activity", krId] });
    void queryClient.invalidateQueries({ queryKey: ["plan", "objective-by-slug", objectiveSlug] });
    void queryClient.invalidateQueries({ queryKey: ["plan", "objectives"] });
  };

  const checkin = useMutation({
    mutationFn: () =>
      createCheckin(krId, {
        value: Number.parseFloat(value),
        confidence,
        note: note.trim() || null,
        blocker: blocker.trim() || null,
      }),
    onSuccess: () => {
      invalidate();
      setNote("");
      setBlocker("");
      setCheckingIn(false);
      toast.success("Check-in recorded");
    },
    onError: (e) => toast.error((e as Error).message),
  });

  // Header ⋮ menu — KR planning edits (mockup KR-detail overflow).
  const patchKr = useMutation({
    mutationFn: (b: Parameters<typeof patchKeyResult>[1]) => patchKeyResult(krId, b),
    onSuccess: invalidate,
    onError: (e) => toast.error((e as Error).message),
  });
  const delKr = useMutation({
    mutationFn: () => deleteKeyResult(krId),
    onSuccess: () => {
      toast.success("Key result deleted");
      void nav({ to: "/plan/o/$objectiveSlug", params: { objectiveSlug } });
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const askKrText = (label: string, cur: string | null | undefined, field: "name" | "unit") => {
    const v = window.prompt(label, cur ?? "");
    if (v != null) patchKr.mutate({ [field]: v.trim() || null } as { name?: string });
  };
  const askKrNum = (
    label: string,
    cur: number | null | undefined,
    field: "current_value" | "target_value" | "weight",
  ) => {
    const v = window.prompt(label, cur != null ? String(cur) : "");
    if (v == null) return;
    const n = Number.parseFloat(v);
    if (!Number.isNaN(n)) patchKr.mutate({ [field]: n });
  };

  // Initiative actions — the entity carries status + progress; the link carries weight.
  const setInitProgress = (it: { initiative_id: string; progress: number }) => {
    const p = window.prompt("Progress (0–100)", String(it.progress ?? 0));
    if (p == null) return;
    patchInitiative(it.initiative_id, { progress: Math.max(0, Math.min(100, Number(p) || 0)) })
      .then(invalidate)
      .catch((e) => toast.error((e as Error).message));
  };
  const renameInit = (it: { initiative_id: string; name: string }) => {
    const n = window.prompt("Initiative name", it.name);
    if (n == null || !n.trim()) return;
    patchInitiative(it.initiative_id, { name: n.trim() })
      .then(invalidate)
      .catch((e) => toast.error((e as Error).message));
  };
  const editInitWeight = (it: { link_id: string; weight: number }) => {
    const w = window.prompt("Weight — share of this KR's execution support (0–100)", String(it.weight ?? 0));
    if (w == null) return;
    reweightInitiativeLink(it.link_id, Number(w) || 0)
      .then(invalidate)
      .catch((e) => toast.error((e as Error).message));
  };
  const promoteInit = (it: { initiative_id: string; name: string }) => {
    if (
      !window.confirm(
        `Promote “${it.name}” to a project? It becomes a contributing project carrying the same weight, and the initiative is retired.`,
      )
    )
      return;
    promoteInitiative(it.initiative_id, {})
      .then((r) => {
        invalidate();
        if (r?.id) nav({ to: "/plan/all-work" });
      })
      .catch((e) => toast.error((e as Error).message));
  };
  const removeInit = (it: { link_id: string; name: string }) => {
    if (!window.confirm(`Remove initiative “${it.name}” from this key result?`)) return;
    unlinkInitiative(it.link_id)
      .then(invalidate)
      .catch((e) => toast.error((e as Error).message));
  };

  const krName = detail.data?.key_result.name;
  useDocumentTitle([krName ? [refOf(krId), krName].filter(Boolean).join(" ") : "Key result", objName]);

  if (detail.isLoading) {
    return (
      <DeskScreen narrow>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-40 w-full" />
      </DeskScreen>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <DeskScreen narrow>
        <Link to="/plan/o/$objectiveSlug" params={{ objectiveSlug }} className="back">
          ← {objName}
        </Link>
        <p className="sec-note" style={{ color: "var(--color-semantic-error)" }}>
          {(detail.error as Error)?.message ?? "Key result not found."}
        </p>
      </DeskScreen>
    );
  }

  const { key_result: kr, checkins, contributing_projects, initiatives, related_krs } = detail.data;
  // Page-body write gate (editor+). Viewers/commenters see read-only content.
  const mayEdit = canEdit(detail.data.my_role);
  // Distance still to travel IN THE GOAL'S DIRECTION. A KR with a baseline
  // above its target is a decrease-is-good one (cost per case, agency hours),
  // and "to go" there counts DOWN from current — the plain target-minus-current
  // reads 0 and the line vanishes. Without a baseline, unchanged.
  const decreasing =
    kr.baseline_value != null && kr.target_value != null && kr.baseline_value > kr.target_value;
  const remaining =
    kr.target_value == null
      ? null
      : decreasing
        ? Math.max(0, kr.current_value - kr.target_value)
        : Math.max(0, kr.target_value - kr.current_value);
  const heroFill = barFill(kr.progress);

  // Weighted execution-support signal — a blend of contributing projects AND
  // initiatives (each carries a per-link weight). Separate from the KR's metric
  // progress; derived entirely client-side.
  const contributors: Contributor[] = [
    ...contributing_projects.map((p) => ({
      id: p.project_id,
      name: p.project_name,
      progress: p.progress,
      weight: p.weight,
      kind: "project" as const,
    })),
    ...initiatives.map((it) => ({
      id: it.initiative_id,
      name: it.name,
      progress: it.progress,
      weight: it.weight,
      kind: "initiative" as const,
    })),
  ];
  const exec = rollup(contributors);
  const execBy = Object.fromEntries(exec.items.map((it) => [it.id, it]));
  const kindById = Object.fromEntries(contributors.map((c) => [c.id, c.kind]));

  // Dependencies → auto-derived risks (client-side, from data already present).
  // `period` is an additive (passthrough) field on the KR row when present.
  const period = (kr as { period?: string | null }).period ?? "target date";
  const risks: string[] = [];
  if (kr.progress < 60) risks.push(`Behind pace to hit target by ${period}`);
  contributing_projects
    .filter((p) => p.status === "at_risk" || p.status === "blocked")
    .forEach((p) => risks.push(`${p.project_name} is ${p.status === "blocked" ? "blocked" : "at risk"}`));
  if (contributing_projects.length < 2) risks.push("Thin coverage — few contributing projects");

  const krRef = refOf(krId);

  // "Last checked in" label for the Check-ins section header — prefers the KR's
  // last_checkin summary, else the most recent check-in row. Stale after 8 days.
  const lastCheckinAt = kr.last_checkin?.created_at ?? checkins[0]?.created_at ?? null;
  const lastCheckinLabel = (() => {
    if (!lastCheckinAt) return { text: "No check-ins yet", stale: false };
    const days = Math.floor((Date.now() - new Date(lastCheckinAt).getTime()) / 86_400_000);
    if (days >= 8) return { text: `No check-in in ${days} days`, stale: true };
    return { text: `Last checked in ${fmtDate(lastCheckinAt)}`, stale: false };
  })();
  const valid = value !== "" && !Number.isNaN(Number.parseFloat(value));

  // Header (identity + hero progress + owner/period) lives ABOVE the sheet
  // (mockup "Canvas Is Sacred"); the work below floats on the paper.
  const toolbar = (
    <div className="desk-head">
      <Link to="/plan/o/$objectiveSlug" params={{ objectiveSlug }} className="back">
        ← {objName}
      </Link>
      <div className="crumb">
        KEY RESULT
        {krRef && (
          <>
            {" "}
            <span className="sep">·</span> {krRef}
          </>
        )}
      </div>

      {/* Header band — uppercase confidence pill, title, hero progress (mockup .krc-*) */}
      <div className="krc-head">
        <div className="wpx-top">
          {kr.confidence ? (
            <StatusPill
              status={kr.confidence}
              label={confidenceLabel(kr.confidence)}
              lg
              className="krc-status"
            />
          ) : (
            <span />
          )}
          {mayEdit && (
          <div className="wpx-actions">
            <button
              type="button"
              className="btn sm"
              onClick={() => setCheckingIn((v) => !v)}
            >
              <Plus size={14} /> Check in
            </button>
            <div className="kr-more-wrap" ref={krMenuRef}>
              <button
                type="button"
                className="pc-overflow"
                title="Actions"
                onClick={() => setKrMenuOpen((o) => !o)}
              >
                <MoreVertical size={16} />
              </button>
              {krMenuOpen && (
                <div className="card-menu">
                  <button
                    type="button"
                    className="cm-item"
                    onClick={() => {
                      setKrMenuOpen(false);
                      askKrNum("Current value", kr.current_value, "current_value");
                    }}
                  >
                    Edit progress
                  </button>
                  <button
                    type="button"
                    className="cm-item"
                    onClick={() => {
                      setKrMenuOpen(false);
                      askKrText("Key result title", kr.name, "name");
                    }}
                  >
                    Edit title
                  </button>
                  <button
                    type="button"
                    className="cm-item"
                    onClick={() => {
                      setKrMenuOpen(false);
                      askKrNum("Target value", kr.target_value, "target_value");
                    }}
                  >
                    Update target
                  </button>
                  <button
                    type="button"
                    className="cm-item"
                    onClick={() => {
                      setKrMenuOpen(false);
                      askKrText("Unit (e.g. %, users, $)", kr.unit, "unit");
                    }}
                  >
                    Set unit
                  </button>
                  <button
                    type="button"
                    className="cm-item"
                    onClick={() => {
                      setKrMenuOpen(false);
                      askKrNum("Weight toward the objective (share %)", kr.weight, "weight");
                    }}
                  >
                    Set weight
                  </button>
                  <div className="cm-sep" />
                  <button
                    type="button"
                    className="cm-item danger"
                    onClick={() => {
                      setKrMenuOpen(false);
                      if (window.confirm(`Delete "${kr.name}"?`)) delKr.mutate();
                    }}
                  >
                    Delete key result
                  </button>
                </div>
              )}
            </div>
          </div>
          )}
        </div>
        <h1 className="page krc-h1">
          {krRef && <span className="ref-id">{krRef}</span>}
          {kr.name}
        </h1>

        <div className="krc-prog">
          <div className="krc-prog-top">
            <span className="krc-prog-pct">{kr.progress}%</span>
            <span className="krc-prog-lbl">of target</span>
            <span className="krc-prog-rem">
              {withUnit(kr.current_value, kr.unit)} /{" "}
              {kr.target_value == null ? "—" : withUnit(kr.target_value, kr.unit)}
              {remaining != null && remaining > 0 && (
                <> · {withUnit(remaining, kr.unit)} to go</>
              )}
            </span>
          </div>
          <div className="krc-hero-bar">
            <span
              className={`krc-hero-fill ${heroFill}`}
              style={{ width: `${kr.progress}%` }}
            />
          </div>
        </div>

        {/* Owner + period — inherited from the parent objective (mockup .krc-meta). */}
        <div className="krc-meta">
          {objOwner && (
            <span className="krc-meta-item">
              <span className="mini-avatar sm">{initials(objOwner)}</span> {objOwner}
            </span>
          )}
          {objPeriod && (
            <span className="krc-meta-item">
              <Target size={13} /> {objPeriod}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <DeskScreen narrow toolbar={toolbar}>
      {/* Contributing Work — FIRST after the header: the execution work driving the KR.
          Project progress is the primary %; objective WEIGHT is secondary (tooltip + the
          collapsible contribution breakdown only — never a row chip). */}
      <div className="section krc-contrib">
        <h2 className="sec">
          Contributing Work{" "}
          <span className="cell-dim" style={{ fontWeight: 400 }}>
            ({contributing_projects.length})
          </span>
        </h2>
        {contributing_projects.length ? (
          <>
            <div className="krc-proj-list">
              {contributing_projects.map((p) => {
                const tone = pillClass(p.status);
                const share = execBy[p.project_id]?.weightPct;
                return (
                  <div
                    key={p.link_id}
                    className="krc-proj"
                    title={share != null ? `Contributes ${share}% to this KR` : undefined}
                  >
                    <div className="krc-proj-main">
                      <div className="krc-proj-name">{p.project_name}</div>
                      <div className="krc-proj-sub">
                        <span className={`krc-dot ${tone}`} /> {humanizeStatus(p.status)}
                      </div>
                      <div className="krc-proj-bar">
                        <span
                          className={`krc-proj-fill ${barFill(p.progress)}`}
                          style={{ width: `${p.progress}%` }}
                        />
                      </div>
                    </div>
                    <div className="krc-proj-prog2">
                      <b>{p.progress}%</b>
                      <span>progress</span>
                    </div>
                  </div>
                );
              })}
            </div>
            {contributing_projects.length || initiatives.length ? (
              <>
                <button
                  type="button"
                  className="obj-roll-toggle"
                  onClick={() => setExecOpen((o) => !o)}
                  title="Per-contributor weight & contribution to this KR's execution support — secondary to project progress"
                >
                  Contribution breakdown
                  <span className="obj-roll-caret">{execOpen ? "▴" : "▾"}</span>
                </button>
                {execOpen && (
                  <div className="obj-roll" style={{ marginTop: 10 }}>
                    <div className="obj-roll-row obj-roll-head">
                      <span>Contributor</span>
                      <span>Progress</span>
                      <span>Weight</span>
                      <span>Contribution</span>
                    </div>
                    {exec.items.map((it) => (
                      <div className="obj-roll-row" key={`${it.kind}-${it.id}`}>
                        <span className="obj-roll-name">
                          {it.name}
                          {kindById[it.id] === "initiative" && (
                            <span className="krc-contrib-kind" title="Initiative (pre-project bet)">
                              Initiative
                            </span>
                          )}
                        </span>
                        <span>{it.progress}%</span>
                        <span className="obj-roll-w">{it.weightPct}%</span>
                        <span className="obj-roll-c">{Math.round(it.contribution)}%</span>
                      </div>
                    ))}
                    <div className="obj-roll-row obj-roll-total">
                      <span>Execution support</span>
                      <span />
                      <span />
                      <span>{exec.progress}%</span>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </>
        ) : (
          <p className="sec-note">
            No contributing work linked yet — link projects or workspaces from the objective page.
          </p>
        )}
      </div>

      {/* Check-ins — the update ritual + the story behind the number. The composer
          is merged into this section (mockup pattern), revealed by either "+ Check in". */}
      <div className="section krc-checkins">
        <div className="wp-sec-head">
          <h2 className="sec" style={{ margin: 0 }}>
            Check-ins{" "}
            <span className="cell-dim" style={{ fontWeight: 400 }}>
              ({checkins.length})
            </span>
            <span className={`krc-checkin-when ${lastCheckinLabel.stale ? "stale" : ""}`}>
              {lastCheckinLabel.text}
            </span>
          </h2>
          {mayEdit && !checkingIn && (
            <button type="button" className="btn sm" onClick={() => setCheckingIn(true)}>
              {checkins.length ? "+ Check in" : "+ Add first check-in"}
            </button>
          )}
        </div>

        {checkingIn && (
          <div className="checkin-composer">
            <div className="ci-top">
              <div className="ci-field">
                <span className="ci-label">Current value</span>
                <span className="ci-val">
                  <input
                    type="number"
                    className="input"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                  />
                  <span className="ci-unit">
                    / {kr.target_value == null ? "—" : fmt(kr.target_value)} {kr.unit ?? ""}
                  </span>
                </span>
              </div>
              <div className="ci-field">
                <span className="ci-label">Confidence</span>
                <div className="ci-conf">
                  {CONFIDENCE_OPTIONS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setConfidence(c)}
                      className={`ci-conf-btn ${pillClass(c)}${confidence === c ? " sel" : ""}`}
                    >
                      {confidenceLabel(c)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <input
              className="input ci-note"
              autoFocus
              placeholder="What changed? (short note)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <input
              className="input ci-blocker"
              placeholder="Blocker / risk (optional)"
              value={blocker}
              onChange={(e) => setBlocker(e.target.value)}
            />
            <div className="ci-actions">
              <button
                type="button"
                className="btn primary sm"
                disabled={!valid || !note.trim() || checkin.isPending}
                onClick={() => checkin.mutate()}
              >
                {checkin.isPending ? "Recording…" : "Check in"}
              </button>
              <button type="button" className="btn sm" onClick={() => setCheckingIn(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {checkins.length === 0 && !checkingIn ? (
          <p className="sec-note">
            No check-ins yet — record the first update to start this key result's history.
          </p>
        ) : (
          <div className="checkin-feed">
            {checkins.map((c) => (
              <CheckinItem key={c.id} c={c} unit={kr.unit} />
            ))}
          </div>
        )}
      </div>

      {/* Planned Initiatives — potential bets, BELOW committed contributing projects.
          Initiative progress is the only %; weight lives in the breakdown above / tooltip. */}
      <div className="section">
        <h2 className="sec">
          Planned Initiatives{" "}
          <span className="cell-dim" style={{ fontWeight: 400 }}>
            ({initiatives.length})
          </span>
        </h2>
        <p className="sec-note" style={{ marginTop: -2 }}>
          Future bets that may support this KR if promoted into execution.
        </p>
        {initiatives.length ? (
          <div className="krc-init-list">
            {initiatives.map((it) => (
              <div
                className="krc-init"
                key={it.link_id}
                title={`Contributes ${execBy[it.initiative_id]?.weightPct ?? it.weight ?? 0}% to this KR`}
              >
                <span className="krc-init-name">{it.name}</span>
                <span className="krc-init-prog" title="Initiative progress">
                  <span className="krc-init-bar">
                    <span
                      className={`krc-init-fill ${barFill(it.progress || 0)}`}
                      style={{ width: `${it.progress || 0}%` }}
                    />
                  </span>
                  {it.progress || 0}% progress
                </span>
                <span className={`krc-init-status ${initStatusCls(it.status)}`}>
                  {humanizeStatus(it.status || "proposed")}
                </span>
                {mayEdit && (
                  <MoreMenu
                    title="Initiative actions"
                    items={[
                      { label: "Rename", onClick: () => renameInit(it) },
                      { label: "Set progress", onClick: () => setInitProgress(it) },
                      { label: "Edit weight", onClick: () => editInitWeight(it) },
                      { label: "Promote to Project", onClick: () => promoteInit(it) },
                      { sep: true },
                      { label: "Remove", danger: true, onClick: () => removeInit(it) },
                    ]}
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="krc-empty">
            No initiatives linked yet. Initiatives are proposed bets that support this key result and
            can later be promoted to projects.
          </p>
        )}
      </div>

      {/* Risks & related KRs — supporting context (two-column .krc-deps) */}
      <div className="section krc-deps-section">
        <h2 className="sec">Risks &amp; related KRs</h2>
        <div className="krc-deps two">
          <div className="krc-dep-col">
            <div className="krc-dep-h">Risks</div>
            {risks.length ? (
              risks.map((r, i) => (
                <div key={i} className="krc-dep-item risk">
                  ⚠ {r}
                </div>
              ))
            ) : (
              <div className="krc-dep-empty">No risks flagged</div>
            )}
          </div>
          <div className="krc-dep-col">
            <div className="krc-dep-h">Related Key Results</div>
            {related_krs.length ? (
              related_krs.map((r) => (
                <Link
                  to="/plan/o/$objectiveSlug/kr/$krId"
                  params={{ objectiveSlug, krId: r.id }}
                  key={r.id}
                  className="krc-dep-item"
                >
                  {refOf(r.id) && <span className="ref-id">{refOf(r.id)}</span>}
                  {r.name}
                </Link>
              ))
            ) : (
              <div className="krc-dep-empty">None</div>
            )}
          </div>
        </div>
      </div>

      {/* KR-level comments moved to the unified right rail (Comments tab). The
          center column keeps only the per-check-in discussions below. */}

      {/* Activity — real audit log (progress raised, projects linked, check-ins).
          Collapsed by default; sits at the very bottom, after Comments, mirroring
          the mockup's .feed activity log. */}
      <div className="section">
        <div className="wp-sec-head">
          <h2 className="sec" style={{ margin: 0 }}>
            Activity{" "}
            <span className="cell-dim" style={{ fontWeight: 400 }}>
              ({activity.length})
            </span>
          </h2>
          {activity.length > 0 && (
            <button type="button" className="wp-collapse" onClick={() => setActOpen((o) => !o)}>
              {actOpen ? "Collapse ▲" : "Expand ▼"}
            </button>
          )}
        </div>
        {activity.length === 0 ? (
          <p className="sec-note">No activity yet — check-ins and edits will appear here.</p>
        ) : !actOpen ? (
          <p className="sec-note">
            {activity.length} update{activity.length === 1 ? "" : "s"} hidden — the check-ins
            above carry the progress story.
          </p>
        ) : (
          <div className="feed">
            {activity.map((a) => (
              <div className="feed-item" key={a.id}>
                <span className="mini-avatar">
                  {initials(a.actor_agent ?? a.actor_name ?? "?")}
                </span>
                <span className="feed-text">
                  <b>{a.actor_agent ?? a.actor_name ?? "Someone"}</b> {a.verb}{" "}
                  {a.target ? <span className="feed-target">{a.target}</span> : null}
                </span>
                <span className="feed-at">{relAt(a.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </DeskScreen>
  );
}
